import { useCallback, useEffect, useMemo, useState } from 'react';
import { isEmpty, find, size } from 'lodash';

import { Button, Icon, showToast, Skeleton } from '@repo/ui';
import { cn, dateUtil } from '@xtnd/web/shared/utils';
import { AttorneyResponseAction, AttorneyResponseStatus, UserRole } from '@repo/types';
import { AttorneyResponse, Timekeeper } from '../../types';
import { AttorneyResponseItem } from './AttorneyResponseItem';
import { usePatchAttorneyResponse } from '../../queries/invoiceQuery';
import { useMatterInvoiceSummaryFilesQuery } from '../../queries/mattersQuery';

// --- Types ---

type SubmittedBannerProps = {
  submittedBy: string;
  submittedAt: string;
  submittedByRole?: UserRole;
};

interface TimekeeperWithResponse extends Timekeeper {
  response?: AttorneyResponse;
  status: 'SUBMITTED' | 'PENDING' | 'NOT_SUBMITTED';
}

interface AttorneyResponsesContentProps {
  timekeepers: Timekeeper[];
  attorneyResponses: AttorneyResponse[];
  isLoading?: boolean;
  selectedTimekeeperId?: string | null;
  onTimekeeperSelect?: (timekeeperId: string) => void;
  allowViewAll?: boolean;
  autoSelectFirst?: boolean;
  className?: string;
  canEditMode?: boolean;
  onEditResponse?: (params: { invoiceId: string; appealId: string; timekeeperId: string }) => void;
  invoiceId?: string;
  appealId?: string;
  renderDelegateSection?: (timekeeperId: string) => React.ReactNode;
  currentAttyCode?: string | null;
}

// --- Presentation Components ---

const SubmittedBanner = ({ submittedBy, submittedAt, submittedByRole }: SubmittedBannerProps) => {
  const formattedDate = dateUtil.formatDate(
    new Date(submittedAt),
    dateUtil.DateFormat.US_WITH_TIME
  );

  let message = `Attorney ${submittedBy} submitted at ${formattedDate}. You are currently in read-only mode.`;

  if (submittedByRole === UserRole.ADMIN) {
    message = `This response was submitted by <strong>${submittedBy}</strong> on <strong>${formattedDate}</strong>. The page is now in read-only mode.`;
  }

  return (
    <div className="mb-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
      <div className="flex items-center gap-2 text-sm text-blue-700 dark:text-blue-400">
        <Icon name="HiInformationCircle" size="sm" />
        <span dangerouslySetInnerHTML={{ __html: message }}></span>
      </div>
    </div>
  );
};

const PendingBanner = () => (
  <div className="mb-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
    <div className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400">
      <Icon name="HiClock" size="sm" />
      <div className="flex flex-col">
        <span className="font-medium">Pending</span>
        <span className="text-xs mt-1">
          This file has not been formally submitted by the attorney. The content below may be empty
          or still in draft form.
        </span>
      </div>
    </div>
  </div>
);

const InvoiceNotFound = () => (
  <div className="flex-1 h-full flex flex-col items-center justify-center p-8 text-center">
    <div className="relative mb-8 group">
      <div className="absolute inset-0 bg-xtnd-blue/10 dark:bg-xtnd-blue/5 rounded-full blur-3xl transform scale-150 animate-pulse" />
      <div className="relative flex items-center justify-center w-32 h-32">
        <div className="absolute inset-0 border-2 border-dashed border-xtnd-blue/30 dark:border-xtnd-blue/20 rounded-full animate-[spin_10s_linear_infinite]" />
        <div className="relative flex items-center justify-center w-24 h-24 bg-xtnd-white dark:bg-xtnd-dark-800 rounded-2xl shadow-xl border border-gray-50 dark:border-xtnd-dark-700 transform group-hover:rotate-6 transition-transform duration-300">
          <Icon name="HiXCircle" size="lg" className="text-xtnd-blue dark:text-xtnd-blue-400" />
        </div>
        <div className="absolute -bottom-2 -right-2 bg-xtnd-blue text-xtnd-white p-2 rounded-lg shadow-lg animate-bounce">
          <Icon name="HiDocumentText" size="sm" />
        </div>
      </div>
    </div>
    <div className="space-y-3 mb-10">
      <h3 className="text-lg font-bold text-xtnd-dark dark:text-xtnd-light tracking-tight leading-tight">
        No results found
      </h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
        We couldn't find the appeal you're looking for. Please check the ID or try another search.
      </p>
    </div>
  </div>
);

const TimekeepersNotFound = () => (
  <div className="flex-1 h-full flex flex-col gap-6 p-4 bg-background-light dark:bg-xtnd-dark-900">
    <div className="flex gap-4 h-full">
      <div className="flex flex-col gap-6 w-1/3">
        <div className="flex gap-4">
          <SummaryCard
            label="SUBMITTED"
            value={0}
            colorClass="text-green-600 dark:text-green-400"
          />
          <SummaryCard
            label="PENDING"
            value={0}
            colorClass="text-orange-600 dark:text-orange-400"
          />
        </div>
        <div className="flex-1 flex flex-col items-center justify-center bg-white dark:bg-xtnd-dark-800 rounded-lg border border-gray-200 dark:border-xtnd-dark-700 p-8 text-center shadow-sm shadow-xtnd-blue/50">
          <div className="relative mb-6">
            <div className="flex items-center justify-center w-20 h-20 bg-gray-100 dark:bg-xtnd-dark-700 rounded-full">
              <Icon name="HiFolder" size="lg" className="text-gray-400 dark:text-gray-500" />
            </div>
          </div>
          <h3 className="text-base font-bold text-xtnd-dark dark:text-xtnd-light mb-2">
            No responses found.
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
            The list is empty or no data has been created yet.
          </p>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center bg-white dark:bg-xtnd-dark-800 rounded-lg border border-gray-200 dark:border-xtnd-dark-700 p-8 shadow-xtnd-box shadow-sm shadow-xtnd-blue/50">
        <div className="w-full max-w-2xl text-center">
          <div className="flex flex-col items-center mb-8">
            <div className="flex items-center justify-center w-24 h-24 bg-gray-100 dark:bg-xtnd-dark-700 rounded-full mb-6">
              <Icon name="HiFolder" size="xl" className="text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-xl font-bold text-xtnd-dark dark:text-xtnd-light mb-3">
              No data found.
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">
              There are currently no responses from attorneys in the system.
            </p>
          </div>
          <div className="flex flex-row gap-4">
            <InfoBox
              title="FOR TEAM"
              message="Data will appear here once attorneys begin the appeal process."
              color="blue"
            />
            <InfoBox
              title="TIMELINE"
              message="Please review the status of the related invoices to understand why no responses are available yet."
              color="orange"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Helper Components for Cleanliness
const SummaryCard = ({
  label,
  value,
  colorClass,
}: {
  label: string;
  value: number;
  colorClass: string;
}) => (
  <div className="flex-1 bg-white dark:bg-xtnd-dark-800 rounded-lg border border-gray-200 dark:border-xtnd-dark-700 p-4 shadow-sm shadow-xtnd-blue/50">
    <div className="flex flex-col text-center">
      <span className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
        {label}
      </span>
      <span className={cn('text-lg font-bold mt-2', colorClass)}>{value}</span>
    </div>
  </div>
);

const InfoBox = ({
  title,
  message,
  color,
}: {
  title: string;
  message: string;
  color: 'blue' | 'orange';
}) => {
  const styles =
    color === 'blue'
      ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400'
      : 'bg-orange-50 border-orange-200 text-orange-700 dark:bg-orange-900/20 dark:border-orange-800 dark:text-orange-400';

  const textStyles =
    color === 'blue' ? 'text-blue-600 dark:text-blue-300' : 'text-orange-600 dark:text-orange-300';

  return (
    <div className={cn('w-1/2 border rounded-lg p-4 text-left', styles)}>
      <h4 className="text-sm font-bold mb-2">{title}</h4>
      <p className={cn('text-sm leading-relaxed', textStyles)}>{message}</p>
    </div>
  );
};

export const AttorneyResponsesSkeleton = () => (
  <div className="flex h-full gap-6 p-6">
    <div className="w-1/3 flex flex-col gap-6">
      <div className="flex gap-4">
        <Skeleton className="h-20 flex-1" />
        <Skeleton className="h-20 flex-1" />
      </div>
      {[1, 2].map(i => (
        <div key={i} className="rounded-lg border border-gray-200 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-16" />
          </div>
          <Skeleton className="h-4 w-48" />
          <div className="flex gap-2">
            <Skeleton className="h-5 w-20 rounded-full" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
      ))}
    </div>
    <div className="flex-1 rounded-lg border border-gray-200 p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-5 w-64" />
          <Skeleton className="h-4 w-48" />
        </div>
        <Skeleton className="h-10 w-36 rounded-md" />
      </div>
      <div className="grid grid-cols-12 gap-4">
        <Skeleton className="h-4 col-span-2" />
        <Skeleton className="h-4 col-span-6" />
        <Skeleton className="h-4 col-span-4" />
      </div>
      {[1, 2].map(i => (
        <div key={i} className="grid grid-cols-12 gap-4 items-start border-t pt-4">
          <Skeleton className="h-4 col-span-2" />
          <div className="col-span-6 space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-2/3" />
          </div>
          <div className="col-span-4 space-y-2">
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// --- Main Component ---

export const AttorneyResponsesContent = ({
  timekeepers,
  attorneyResponses,
  isLoading = false,
  selectedTimekeeperId,
  onTimekeeperSelect,
  allowViewAll = false,
  autoSelectFirst = true,
  className,
  canEditMode = false,
  onEditResponse,
  invoiceId,
  appealId,
  renderDelegateSection,
  currentAttyCode,
}: AttorneyResponsesContentProps) => {
  const [internalSelectedId, setInternalSelectedId] = useState<string | null>(null);
  const { mutateAsync: patchAttorneyResponse, isPending: isPatchingAttorneyResponse } =
    usePatchAttorneyResponse();

  const currentSelectedId =
    selectedTimekeeperId !== undefined ? selectedTimekeeperId : internalSelectedId;

  const setSelectedId = (id: string | null) => {
    if (onTimekeeperSelect && id) {
      onTimekeeperSelect(id);
    } else {
      setInternalSelectedId(id);
    }
  };
  const {
    data: existingFilesResponse,
    isLoading: isLoadingExistingFiles,
  } = useMatterInvoiceSummaryFilesQuery(invoiceId || '', { enabled: !!invoiceId });
  const existingFiles = existingFilesResponse?.data || [];
  // Logic: Map responses to timekeepers
  const timekeepersWithResponses = useMemo<TimekeeperWithResponse[]>(() => {
    if (isEmpty(timekeepers)) return [];
    return timekeepers.map(timekeeper => {
      const response = find(attorneyResponses, { id: timekeeper.id });
      let status: 'SUBMITTED' | 'PENDING' | 'NOT_SUBMITTED' = 'NOT_SUBMITTED';
      if (response) {
        status = response.createdAt ? 'SUBMITTED' : 'PENDING';
      }
      return { ...timekeeper, response, status };
    });
  }, [timekeepers, attorneyResponses]);

  // Logic: Merge orphaned responses
  const allResponsesWithTimekeepers = useMemo(() => {
    if (!allowViewAll) return timekeepersWithResponses;
    const orphanedResponses = attorneyResponses.filter(
      r => !timekeepers.find(tk => tk.id === r.id)
    );
    const orphanedAsTimekeepers: TimekeeperWithResponse[] = orphanedResponses.map(response => ({
      id: response.id,
      name: response.createdBy || 'Unknown Attorney',
      email: response.createdBy || '',
      response,
      status: response.createdAt ? 'SUBMITTED' : 'PENDING',
    }));
    return [...timekeepersWithResponses, ...orphanedAsTimekeepers];
  }, [timekeepersWithResponses, attorneyResponses, allowViewAll, timekeepers]);

  const summary = useMemo(
    () => ({
      submitted: allResponsesWithTimekeepers.filter(t => t.status === 'SUBMITTED').length,
      pending: allResponsesWithTimekeepers.filter(
        t => t.status === 'PENDING' || t.status === 'NOT_SUBMITTED'
      ).length,
    }),
    [allResponsesWithTimekeepers]
  );

  const selectedTimekeeper = useMemo(() => {
    if (!currentSelectedId) return null;
    if (currentSelectedId.startsWith('timekeeper-')) {
      const timekeeperId = currentSelectedId.replace('timekeeper-', '');
      return allResponsesWithTimekeepers.find(t => t.id === timekeeperId) || null;
    }
    return allResponsesWithTimekeepers.find(t => t.response?.id === currentSelectedId) || null;
  }, [currentSelectedId, allResponsesWithTimekeepers]);

  const selectedResponse = selectedTimekeeper?.response || null;

  const isOwnResponse = useMemo(() => {
    if (!currentAttyCode || !selectedTimekeeper) return false;
    return selectedTimekeeper.id === currentAttyCode || selectedTimekeeper.delegate === currentAttyCode;
  }, [currentAttyCode, selectedTimekeeper]);

  // Auto-selection Effect
  useEffect(() => {
    if (!autoSelectFirst || size(allResponsesWithTimekeepers) === 0 || currentSelectedId) return;
    const first = allResponsesWithTimekeepers[0];
    if (first) {
      setSelectedId(first.response ? first.response.id : `timekeeper-${first.id}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelectedId, allResponsesWithTimekeepers, autoSelectFirst]);

  const handleRequestRevision = useCallback(
    async ({ timekeeperId, status }: { timekeeperId: string; status: AttorneyResponseStatus }) => {
      if (!invoiceId || !appealId) {
        showToast.error('Invalid invoice or appeal ID');
        return;
      }
      await patchAttorneyResponse({ invoiceId, appealId, timekeeperId, payload: { status } });
    },
    [invoiceId, appealId, patchAttorneyResponse]
  );

  // Conditional Rendering logic preserved
  if (isLoading) return <AttorneyResponsesSkeleton />;
  if (isEmpty(timekeepers) && isEmpty(attorneyResponses)) return <InvoiceNotFound />;
  if (isEmpty(allResponsesWithTimekeepers)) return <TimekeepersNotFound />;
  
  return (
    <div
      className={cn(
        'grid grid-cols-[1fr_2fr] gap-6 h-full bg-background-light dark:bg-xtnd-dark-900',
        className
      )}
    >
      {/* Left Sidebar */}
      <div className="flex min-h-[60vh] flex-col gap-4 border-r border-gray-200 dark:border-xtnd-dark-700 p-4">
        <div className="flex gap-4">
          <SummaryCard
            label="SUBMITTED"
            value={summary.submitted}
            colorClass="text-green-600 dark:text-green-400"
          />
          <SummaryCard
            label="PENDING"
            value={summary.pending}
            colorClass="text-orange-600 dark:text-orange-400"
          />
        </div>

        <div className="flex-1 overflow-y-auto space-y-4">
          {allResponsesWithTimekeepers.map(tk => {
            const isSelected =
              tk.response?.id === currentSelectedId ||
              (!tk.response && currentSelectedId === `timekeeper-${tk.id}`);
            return (
              <button
                key={tk.id}
                type="button"
                onClick={() => setSelectedId(tk.response ? tk.response.id : `timekeeper-${tk.id}`)}
                className={cn(
                  'p-4 rounded-lg border cursor-pointer transition-all text-left w-full shadow-sm shadow-xtnd-blue/50',
                  isSelected
                    ? 'bg-xtnd-blue/10 dark:bg-xtnd-blue/20 border-xtnd-blue dark:border-xtnd-blue-400 shadow-md'
                    : 'bg-white dark:bg-xtnd-dark-800 border-gray-200 dark:border-xtnd-dark-700 hover:border-xtnd-blue/50 dark:hover:border-xtnd-blue-400/50',
                  !tk.response && 'opacity-75'
                )}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <span
                    className={cn(
                      'px-2 py-0.5 rounded-full text-xs font-medium',
                      tk.status === 'SUBMITTED'
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                        : tk.status === 'PENDING'
                          ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400'
                          : 'bg-gray-100 dark:bg-xtnd-dark-700 text-gray-600 dark:text-gray-400'
                    )}
                  >
                    {tk.status === 'SUBMITTED'
                      ? 'SUBMITTED'
                      : tk.status === 'PENDING'
                        ? 'PENDING'
                        : 'NOT SUBMITTED'}
                  </span>
                </div>
                <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light mb-1">
                  {tk.name}
                  {tk.initials && ` (${tk.initials})`}
                </div>
                {tk.response && (
                  <div className="flex items-center gap-2 mt-2">
                    <span
                      className={cn(
                        'px-2 py-0.5 rounded text-xs font-medium',
                        tk.response.appealAction === AttorneyResponseAction.APPEAL
                          ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                          : 'bg-gray-100 dark:bg-xtnd-dark-700 text-gray-600 dark:text-gray-400'
                      )}
                    >
                      {tk.response.appealAction.replace('_', ' ')}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                      <Icon name="HiClock" size="xs" />
                      {tk.response.createdAt
                        ? dateUtil.formatDate(
                            new Date(tk.response.updatedAt || tk.response.createdAt),
                            dateUtil.DateFormat.US_WITH_TIME
                          )
                        : 'Pending'}
                    </span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Right Content Area */}
      <div className="flex-1 h-full flex flex-col overflow-y-hidden py-4 p-2">
        {selectedTimekeeper ? (
          <>
            {canEditMode && invoiceId && appealId && onEditResponse && (
              <div className="w-full flex gap-x-4 justify-end items-center mb-4">
                {selectedResponse?.status === AttorneyResponseStatus.EDIT_REQUESTED && (
                  <Button
                    loading={isPatchingAttorneyResponse}
                    disabled={isPatchingAttorneyResponse}
                    leftIcon={<Icon name="HiCheck" size="md" />}
                    onClick={() =>
                      handleRequestRevision({
                        timekeeperId: selectedTimekeeper.id,
                        status: AttorneyResponseStatus.EDIT_APPROVED,
                      })
                    }
                    className="flex items-center gap-1 px-4 py-2 !bg-green-500 text-white rounded-full shadow-sm hover:!bg-green-600 transition-all transform hover:-translate-y-0.5"
                  >
                    <span className="text-sm font-medium">Approve Revision</span>
                  </Button>
                )}
                <Button
                  variant="primary"
                  leftIcon={<Icon name="HiPencil" size="md" />}
                  onClick={() =>
                    onEditResponse({ invoiceId, appealId, timekeeperId: selectedTimekeeper.id })
                  }
                  className="flex items-center gap-1 px-4 py-2 bg-xtnd-blue text-white rounded-full shadow-sm hover:bg-xtnd-blue-700 transition-all transform hover:-translate-y-0.5"
                >
                  <span className="text-sm font-medium">Edit Response</span>
                </Button>
              </div>
            )}

            {!canEditMode && isOwnResponse && invoiceId && appealId && (
              <div className="w-full flex gap-x-4 justify-end items-center mb-4">
                {selectedResponse?.status === AttorneyResponseStatus.SUBMITTED && (
                  <Button
                    loading={isPatchingAttorneyResponse}
                    disabled={isPatchingAttorneyResponse}
                    leftIcon={<Icon name="HiExclamationCircle" size="md" />}
                    onClick={() =>
                      handleRequestRevision({
                        timekeeperId: selectedTimekeeper.id,
                        status: AttorneyResponseStatus.EDIT_REQUESTED,
                      })
                    }
                    className="flex items-center px-4 py-2 bg-xtnd-blue text-white rounded-full shadow-sm hover:bg-xtnd-blue-700 transition-all transform hover:-translate-y-0.5"
                  >
                    <span className="text-sm font-bold">Request Revision</span>
                  </Button>
                )}
                {selectedResponse?.status === AttorneyResponseStatus.EDIT_APPROVED && onEditResponse && (
                  <Button
                    variant="primary"
                    leftIcon={<Icon name="HiPencil" size="md" />}
                    onClick={() =>
                      onEditResponse({ invoiceId, appealId, timekeeperId: selectedTimekeeper.id })
                    }
                    className="flex items-center gap-1 px-4 py-2 bg-xtnd-blue text-white rounded-full shadow-sm hover:bg-xtnd-blue-700 transition-all transform hover:-translate-y-0.5"
                  >
                    <span className="text-sm font-medium">Edit Response</span>
                  </Button>
                )}
                {!selectedResponse && onEditResponse && (
                  <Button
                    variant="primary"
                    leftIcon={<Icon name="HiPencil" size="md" />}
                    onClick={() =>
                      onEditResponse({ invoiceId, appealId, timekeeperId: selectedTimekeeper.id })
                    }
                    className="flex items-center gap-1 px-4 py-2 bg-xtnd-blue text-white rounded-full shadow-sm hover:bg-xtnd-blue-700 transition-all transform hover:-translate-y-0.5"
                  >
                    <span className="text-sm font-medium">Edit Response</span>
                  </Button>
                )}
              </div>
            )}

            {renderDelegateSection && selectedTimekeeper && (
              <div className="mb-4">
                {renderDelegateSection(selectedTimekeeper.id)}
              </div>
            )}

            {selectedResponse ? (
              <>
                {selectedResponse.createdBy && (
                  <SubmittedBanner
                    submittedByRole={selectedResponse.submittedByRole}
                    submittedBy={
                      selectedResponse.updatedByName || selectedResponse.createdByName || ''
                    }
                    submittedAt={
                      selectedResponse.updatedAt ||
                      selectedResponse.createdAt ||
                      new Date().toISOString()
                    }
                  />
                )}
                {!selectedResponse.createdAt && <PendingBanner />}
                <AttorneyResponseItem
                  appealAction={selectedResponse.appealAction}
                  lineItems={selectedResponse.lineItems || []}
                  attachments={existingFiles}
                  isLoadingExistingFiles={isLoadingExistingFiles}
                  timekeeperName={selectedTimekeeper.name}
                  submittedBy={
                    selectedResponse.updatedByName ||
                    selectedResponse.createdByName ||
                    selectedResponse.createdBy ||
                    ''
                  }
                  submittedByRole={selectedResponse.submittedByRole}
                  submittedAt={
                    selectedResponse.updatedAt ||
                    selectedResponse.createdAt ||
                    new Date().toISOString()
                  }
                  isSubmitted={!!selectedResponse.createdAt}
                  delegateEmail={selectedResponse.delegateEmail}
                />
              </>
            ) : (
              <>
                <PendingBanner />
                <AttorneyResponseItem
                  appealAction={AttorneyResponseAction.NO_APPEAL}
                  lineItems={[]}
                  attachments={[]}
                  timekeeperName={selectedTimekeeper.name}
                  submittedAt={new Date().toISOString()}
                  isSubmitted={false}
                  isLoadingExistingFiles={isLoadingExistingFiles}
                />
              </>
            )}
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center bg-white dark:bg-xtnd-dark-800 rounded-lg border border-gray-200 dark:border-xtnd-dark-700 p-8 text-center">
            <div className="flex items-center justify-center w-24 h-24 bg-gray-100 dark:bg-xtnd-dark-700 rounded-full mb-6">
              <Icon name="HiDocumentText" size="xl" className="text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-xl font-bold text-xtnd-dark dark:text-xtnd-light mb-3">
              No response selected
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">
              Select a response from the list to view details.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
